import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";

let _pdfjsPromise = null;
function loadPdfjs() {
  if (!_pdfjsPromise) {
    _pdfjsPromise = import("pdfjs-dist/build/pdf").then((mod) => {
      const lib = mod.getDocument ? mod : mod.default;
      lib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${lib.version}/pdf.worker.min.js`;
      return lib;
    });
  }
  return _pdfjsPromise;
}

export function useAttachmentPreview(att, isImg, isPdf) {
  const rowRef = useRef(null);
  const canvasRef = useRef(null);
  const blobRef = useRef(null);
  const [thumb, setThumb] = useState(null);
  const [visible, setVisible] = useState(false);
  const [pdfReady, setPdfReady] = useState(false);

  // Lazy: only load the preview once the row scrolls into view.
  useEffect(() => {
    if (!isImg && !isPdf) return;
    const el = rowRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { rootMargin: "150px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [isImg, isPdf]);

  useEffect(() => {
    if (!visible) return;
    let alive = true;
    let url;
    (async () => {
      try {
        const res = await api.get(`/files/${att.storage_path}`, { responseType: "blob" });
        if (!alive) return;
        blobRef.current = res.data;
        if (isImg) {
          url = URL.createObjectURL(res.data);
          setThumb(url);
        } else if (isPdf) {
          const buf = await res.data.arrayBuffer();
          const pdfjsLib = await loadPdfjs();
          const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
          const page = await pdf.getPage(1);
          const base = page.getViewport({ scale: 1 });
          const vp = page.getViewport({ scale: 44 / base.width });
          const canvas = canvasRef.current;
          if (!canvas || !alive) return;
          canvas.width = vp.width;
          canvas.height = vp.height;
          await page.render({ canvasContext: canvas.getContext("2d"), viewport: vp }).promise;
          if (alive) setPdfReady(true);
        }
      } catch (e) { if (process.env.NODE_ENV === "development") console.warn("Attachment preview failed; showing icon:", e); }
    })();
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [visible, isImg, isPdf, att.storage_path]);

  return { rowRef, canvasRef, blobRef, thumb, pdfReady };
}
